/* eslint-disable react-refresh/only-export-components */
import { createContext, Dispatch, FC, Reducer, useReducer } from 'react';

import {
  ClaimRelationshipResponse,
  ClaimRelationshipWithoutVerificationRequest,
  SendVerificationByContactMethodTokenRequest,
} from 'app/api/apiClient/generated/types';
import { ClaimRelationshipWithoutVerificationError } from 'app/api/hooks/core/v1/payee/payeeRelationships';
import { PropsWithOnlyChildren } from 'app/types';

// Local Typings
interface QuickPayDialogProps {
  isOpen: boolean;
  payeeRelationshipIds: string[];
}

export interface State {
  brokerAuthorizationFormError: ClaimRelationshipWithoutVerificationError | null;
  brokerAuthorizationFormErrorCount: Record<string, number>;
  brokerAuthorizationFormPayload: ClaimRelationshipWithoutVerificationRequest;
  claimMoreBrokersPayeeRelationshipIds: string[];
  isClaimMoreBrokersDialogOpen: boolean;
  isFetchingPreAuthenticatedRelationshipIds: boolean; // old flow
  isRequestingCode: boolean;
  isVerificationDialogOpen: boolean;
  isVerifyingWithInvoiceDetails: boolean;
  preAuthenticatedRelationshipIds: string[]; // old flow
  quickPayDialogProps: QuickPayDialogProps;
  selectedPayeeRelationshipIds: null | string[];
  verifyPayeeContactMethodPayload: SendVerificationByContactMethodTokenRequest;
}

interface GetPreAuthenticatedRelationshipIdsAction {
  payload: {
    data: ClaimRelationshipResponse;
  };
  type: 'getPreAuthenticatedRelationshipIds';
}

interface StartGetPreAuthenticatedRelationshipIdsAction {
  type: 'startGetPreAuthenticatedRelationshipIds';
}

interface ClearPreAuthenticatedRelationshipIdsAction {
  type: 'clearPreAuthenticatedRelationshipIds';
}

interface IncrementBrokerAuthorizationFormErrorCountAction {
  payload: {
    payeeRelationshipId: string;
  };
  type: 'incrementBrokerAuthorizationFormErrorCount';
}

interface ResetAction {
  type: 'reset';
}

interface SetBrokerAuthorizationFormErrorAction {
  payload: {
    brokerAuthorizationFormError: ClaimRelationshipWithoutVerificationError | null;
  };
  type: 'setBrokerAuthorizationFormError';
}

interface SetClaimMoreBrokersPayeeRelationshipIdsAction {
  payload: {
    payeeRelationshipIds: string[];
  };
  type: 'setClaimMoreBrokersPayeeRelationshipIds';
}

interface SetBrokerAuthorizationFormPayloadAction {
  payload: {
    brokerAuthorizationFormPayload: Partial<ClaimRelationshipWithoutVerificationRequest>;
  };
  type: 'setBrokerAuthorizationFormPayload';
}

interface SetIsClaimMoreBrokersDialogOpenAction {
  payload: {
    isClaimMoreBrokersDialogOpen: boolean;
  };
  type: 'setIsClaimMoreBrokersDialogOpen';
}

interface SetIsRequestingCodeAction {
  payload: {
    isRequestingCode: boolean;
  };
  type: 'setIsRequestingCode';
}

interface SetQuickPayDialogPropsAction {
  payload: {
    quickPayDialogProps: QuickPayDialogProps;
  };
  type: 'setQuickPayDialogProps';
}

interface SetIsVerificationDialogOpenAction {
  payload: {
    isVerificationDialogOpen: boolean;
  };
  type: 'setIsVerificationDialogOpen';
}

interface SetIsVerifyingWithInvoiceDetailsAction {
  payload: {
    isVerifyingWithInvoiceDetails: boolean;
  };
  type: 'setIsVerifyingWithInvoiceDetails';
}

interface SetSelectedPayeeRelationshipsAction {
  payload: {
    selectedPayeeRelationships: null | string[];
  };
  type: 'setSelectedPayeeRelationships';
}

interface SetVerifyPayeeContactMethodPayloadAction {
  payload: {
    verifyPayeeContactMethodPayload: Partial<SendVerificationByContactMethodTokenRequest>;
  };
  type: 'setVerifyPayeeContactMethodPayload';
}

type ClaimRelationshipContextAction =
  | ClearPreAuthenticatedRelationshipIdsAction // old flow
  | GetPreAuthenticatedRelationshipIdsAction // old flow
  | IncrementBrokerAuthorizationFormErrorCountAction
  | ResetAction
  | SetBrokerAuthorizationFormErrorAction
  | SetBrokerAuthorizationFormPayloadAction
  | SetClaimMoreBrokersPayeeRelationshipIdsAction
  | SetIsClaimMoreBrokersDialogOpenAction
  | SetIsRequestingCodeAction
  | SetIsVerificationDialogOpenAction
  | SetIsVerifyingWithInvoiceDetailsAction
  | SetQuickPayDialogPropsAction
  | SetSelectedPayeeRelationshipsAction
  | SetVerifyPayeeContactMethodPayloadAction
  | StartGetPreAuthenticatedRelationshipIdsAction; // old flow
export type ClaimRelationshipDispatch = Dispatch<ClaimRelationshipContextAction>;

// Local Variables
const initialState: State = {
  brokerAuthorizationFormError: null,
  brokerAuthorizationFormErrorCount: {},
  brokerAuthorizationFormPayload: {
    invoiceOrLoadAmount: 0,
    invoiceOrLoadNumber: '',
    isTermsAcknowledged: false,
  },
  claimMoreBrokersPayeeRelationshipIds: [],
  isClaimMoreBrokersDialogOpen: false,
  isFetchingPreAuthenticatedRelationshipIds: false,
  isRequestingCode: false,
  isVerificationDialogOpen: false,
  isVerifyingWithInvoiceDetails: false,
  preAuthenticatedRelationshipIds: [],
  quickPayDialogProps: {
    isOpen: false,
    payeeRelationshipIds: [],
  },
  selectedPayeeRelationshipIds: null,
  verifyPayeeContactMethodPayload: {
    payeeContactMethodId: '',
    verificationMethodTypeId: 0,
  },
};

export const ClaimRelationshipContext = createContext(initialState);
// eslint-disable-next-line @stylistic/max-len
export const ClaimRelationshipDispatchContext = createContext<ClaimRelationshipDispatch>(undefined as unknown as ClaimRelationshipDispatch);

ClaimRelationshipContext.displayName = 'ClaimRelationshipState';
ClaimRelationshipDispatchContext.displayName = 'ClaimRelationshipDispatch';

const reducer: Reducer<State, ClaimRelationshipContextAction> = (state, action) => {
  switch (action.type) {
    case 'setClaimMoreBrokersPayeeRelationshipIds': {
      return {
        ...state,
        claimMoreBrokersPayeeRelationshipIds: action.payload.payeeRelationshipIds,
      };
    }
    case 'clearPreAuthenticatedRelationshipIds': {
      return {
        ...state,
        preAuthenticatedRelationshipIds: [],
      };
    }
    case 'incrementBrokerAuthorizationFormErrorCount': {
      const { payeeRelationshipId } = action.payload;

      return {
        ...state,
        brokerAuthorizationFormErrorCount: {
          ...state.brokerAuthorizationFormErrorCount,
          [payeeRelationshipId]: (state.brokerAuthorizationFormErrorCount[payeeRelationshipId] ?? 0) + 1,
        },
      };
    }
    case 'startGetPreAuthenticatedRelationshipIds': {
      return {
        ...state,
        isFetchingPreAuthenticatedRelationshipIds: true,
      };
    }
    case 'getPreAuthenticatedRelationshipIds': {
      return {
        ...state,
        isFetchingPreAuthenticatedRelationshipIds: false,
        preAuthenticatedRelationshipIds: action.payload.data.preAuthenticatedGuids,
      };
    }
    case 'reset': {
      return {
        ...initialState,
      };
    }
    case 'setBrokerAuthorizationFormError': {
      return {
        ...state,
        brokerAuthorizationFormError: action.payload.brokerAuthorizationFormError,
      };
    }
    case 'setBrokerAuthorizationFormPayload': {
      return {
        ...state,
        brokerAuthorizationFormPayload: {
          ...state.brokerAuthorizationFormPayload,
          ...action.payload.brokerAuthorizationFormPayload,
        },
      };
    }
    case 'setIsClaimMoreBrokersDialogOpen': {
      return {
        ...state,
        isClaimMoreBrokersDialogOpen: action.payload.isClaimMoreBrokersDialogOpen,
      };
    }
    case 'setIsRequestingCode': {
      return {
        ...state,
        isRequestingCode: action.payload.isRequestingCode,
      };
    }
    case 'setIsVerificationDialogOpen': {
      return {
        ...state,
        isVerificationDialogOpen: action.payload.isVerificationDialogOpen,
      };
    }
    case 'setQuickPayDialogProps': {
      return {
        ...state,
        quickPayDialogProps: action.payload.quickPayDialogProps,
      };
    }
    case 'setIsVerifyingWithInvoiceDetails': {
      return {
        ...state,
        isVerifyingWithInvoiceDetails: action.payload.isVerifyingWithInvoiceDetails,
      };
    }
    case 'setSelectedPayeeRelationships': {
      return {
        ...state,
        isVerifyingWithInvoiceDetails: false,
        selectedPayeeRelationshipIds: action.payload.selectedPayeeRelationships,
      };
    }
    case 'setVerifyPayeeContactMethodPayload': {
      return {
        ...state,
        verifyPayeeContactMethodPayload: {
          ...state.verifyPayeeContactMethodPayload,
          ...action.payload.verifyPayeeContactMethodPayload,
        },
      };
    }
  }
};

const ClaimRelationshipProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <ClaimRelationshipContext.Provider value={state}>
      <ClaimRelationshipDispatchContext.Provider value={dispatch}>
        {children}
      </ClaimRelationshipDispatchContext.Provider>
    </ClaimRelationshipContext.Provider>
  );
};

export default ClaimRelationshipProvider;
